/**
 * UI element: Drag and drop file upload interface
 * requires: s-ui-filedrop css file
 */

export default class FileDrop {
    #callback = null;
    #error_callback = null;
    #validate = function () { return true; };
    #max_files = 0;
    #max_upload_size = 0;
    #max_file_size = 0;
    #batch_files = false;
    #autoupload = true;
    #translations = {
        max_file_size: 'Maximum file size',
    };

    #allowedMimeTypes = [];
    /** @prop HTMLFormElement */
    #form = null;
    /** @prop HTMLElement This is a div containing the label and input[type=file] */
    #droparea = null;
    /**
     * Tracking files that have been dropped to be uploaded
     */
    #droppedFiles = [];
    /**
     * Files batched into groups so they dont go over the max_upload_size
     */
    #batchedFiles = [];
    /**
     * Tracking batch progress
     */
    #batch = { current: 0, total: 0 };
    /** @prop HTMLElement The actual file input */
    #input = null;
    /** @prop HTMLElement file input label */
    #label = null;
    #progress = null;

    /**
     * Constructor
     * @param {Object} config Configuration object
     * @param {HTMLFormElement} config.form Form that contains the filedrop
     * @param {Function} config.callback Success callback for the form submission
     * @param {Function} [config.error_callback] Error callback for the form submission
     * @param {Function} [config.validate] Validation to be called on form submission
     * @param {Number} [config.max_files=0] maximum number of uploads allowed 0=unlimited
     * @param {Number} [config.max_upload_size=0] maximum bytes you can upload to the server at once (probably set in nginx or php or both) 0=unlimited (i.e. don't check)
     * @param {Number} [config.max_file_size=0] maximum bytes for a single file 0=unlimited (i.e. don't check)
     * @param {Boolean} [config.batch_files=false] should files be batched and uploaded in groups if they exceed the max_upload_size
     * @param {Boolean} [config.autoupload=true] automatically submit the form when a file is dropped/added
     * @param {Object} [config.translations] Language values for various messages.
     * @param {Array} [config.allowedMimeTypes] Mime types of files that are allowed. Empty/unset means any.
     */
    constructor({
        form = null,
        callback = null,
        error_callback = null,
        validate = null,
        max_files = 0,
        max_upload_size = 0,
        max_file_size = 0,
        batch_files = false,
        autoupload = true,
        translations = null,
        allowedMimeTypes = [],
    }) {
        this.#form = form;
        this.#callback = callback;
        this.#error_callback = error_callback;
        if (validate) {
            this.#validate = validate;
        }
        this.#max_files = max_files;
        this.#max_upload_size = max_upload_size;
        this.#max_file_size = max_file_size;
        this.#batch_files = batch_files;
        this.#autoupload = autoupload;
        if (translations) {
            this.#translations = translations;
        }
        this.#allowedMimeTypes = allowedMimeTypes;
        if (!this.#form) {
            return;
        }
        this.#droparea = this.#form.querySelector('.s-ui-filedrop');
        this.#input = this.#droparea?.querySelector('input[type="file"]');
        this.#label = this.#droparea?.querySelector('label');
    }

    /**
     * Update the label to show file names
     */
    #showFiles() {
        if (!this.#input) {
            return;
        }

        const fileArea = this.#droparea.querySelector('.s-ui-filedrop-filenames');
        const fileList = fileArea.querySelector('ul');

        const fileItems = [];
        let total_size = 0;
        this.#droppedFiles.forEach((file) => {
            const item = document.createElement('li');
            item.innerText = file.name;
            fileItems.push(item);
            total_size += file.size;
        });

        fileList.innerHTML = '';
        if (fileItems.length === 0) {
            fileArea.classList.add('hidden');
        } else {
            fileArea.classList.remove('hidden');
            fileItems.forEach((li) => {
                fileList.appendChild(li);
            });
        }
        if (this.#max_files > 0 && fileItems.length > this.#max_files) {
            this.showError(`${this.#max_files} file uploads maximum.`);
        }
        if (!this.#batch_files && this.#max_upload_size > 0 && total_size > this.#max_upload_size) {
            this.showError(`Files total ${this.#bytesToMB(total_size)} MB which exceeds the maximum of ${this.#bytesToMB(this.#max_upload_size)}MB at a time.`);
        }
    }

    /**
     * Resets the form and files and errors
     */
    reset() {
        this.#form.reset();
        this.#removeError();
        this.#droppedFiles = [];
        this.#batch.current = 0;
        this.#batch.total = 0;
        this.#stopProgress();
        this.#showFiles();
        this.#input?.classList.remove('has-focus');
    }

    /**
     * Clear the files only
     */
    #clearFiles() {
        this.#resetFileInput();
        this.#droppedFiles = [];
        this.#batch.current = 0;
        this.#batch.total = 0;
        this.#showFiles();
    }

    /**
     * Reset the input[type=file]
     * the only way to clear a input[type=file] is to reset the whole form or remove/clone the input.
     * For this we will remove/clone, FF won't remove the value/files with a straight clone so we are cloning to a temp form,
     * resetting it, then moving the cloned input to the real form.
     */
    #resetFileInput() {
        if (!this.#input) {
            return;
        }
        const tempForm = document.createElement('FORM');
        const newinput = this.#input.cloneNode(true);
        tempForm.append(newinput);
        tempForm.reset();
        this.#input.replaceWith(newinput);
        this.#input = newinput;
        this.#addInputEvents();
    }

    /**
     * Show an error alert inside the drop area (good for file related errors)
     * @param {String} msg An error message
     */
    showError(msg) {
        if (!this.#droparea) {
            return;
        }
        const msgEl = document.createElement('p');
        msgEl.innerText = msg;
        if (this.#form.querySelector('div.s-ui-filedrop-error')) {
            this.#form.querySelector('div.s-ui-filedrop-error').append(msgEl);
            return;
        }
        const alert = document.createElement('div');
        alert.classList.add('alert');
        alert.classList.add('alert-danger');
        alert.classList.add('s-ui-filedrop-error');
        alert.setAttribute('role', 'alert');
        alert.append(msgEl);
        this.#droparea.prepend(alert);
    }

    /**
     * Empty the error div, which because of the css will cause it to disappear
     */
    #removeError() {
        const errorDiv = this.#form.querySelector('div.s-ui-filedrop-error');
        if (errorDiv) {
            errorDiv.innerHTML = '';
        }
    }

    /**
     * Count how many files have been added to widget
     * @return {Number}
     */
    countFiles() {
        if (!this.#input) {
            return 0;
        }
        return this.#droppedFiles.length;
    }

    /**
     * Are we on the last batch
     * @return {Boolean}
     */
    isLastBatch() {
        return this.#batch.current >= this.#batch.total;
    }

    /**
     * Rev up the progress bar so we can start updating it
     */
    #startProgress() {
        if (!this.#droparea) {
            return;
        }
        if (this.#progress && this.#progress.innerHTML !== '') {
            return;
        }
        let existing = true;
        this.#progress = this.#form.querySelector('div.s-ui-filedrop-progress');
        if (!this.#progress) {
            existing = false;
            this.#progress = document.createElement('DIV');
            this.#progress.classList.add('s-ui-filedrop-progress');
        }
        const label = document.createElement('LABEL');
        label.id = 's-ui-filedrop-progresslabel';
        label.setAttribute('for', 's-ui-filedrop-progbar');
        label.innerText = 'Uploading files';
        const bar = document.createElement('PROGRESS');
        bar.id = 's-ui-filedrop-progbar';
        bar.setAttribute('max', '100');
        bar.value = 0;
        this.#progress.append(label, bar);
        // if the progress bar was not already in the page, we should inject it into the drop area
        if (!existing) {
            this.#droparea.prepend(this.#progress);
        }
    }

    /**
     * Update the progress bar, if there is none, add it.
     * @param {number} loaded bytes uploaded
     * @param {number} total total bytes to upload
     */
    #updateProgress(loaded, total) {
        let val = Math.round((loaded / total) * 100);
        if (isNaN(val) || !isFinite(val)) {
            val = 0;
        }
        const bar = this.#progress.querySelector('progress');
        if (bar) {
            bar.value = val;
        }
        if (this.#batch.total > 1 && val < 50) {
            const label = this.#progress.querySelector('div:first');
            if (label) {
                label.innerHTML = `Uploading file batch (${this.#batch.current}/${this.#batch.total})`;
            }
        }
    }

    /**
     * Stop the progress bar (i.e. close/hide it)
     */
    #stopProgress() {
        if (this.#progress) {
            this.#progress.innerHTML = '';
        }
    }

    /**
     * Convert bytes to MB
     * @param {Number} bytes File in bytes.
     * @return {Number} MB
     */
    #bytesToMB(bytes) {
        return (parseInt(bytes, 10) / (1024 * 1024)).toFixed(1);
    }

    /**
     * Add files from a FileList to the droppedFiles array
     * checking size limitations in the process
     * @param {FileList} filelist A FileList object (Array-like)
     * @return {Boolean}
     */
    #addFiles(filelist) {
        if (this.#max_files > 0 && (this.countFiles() + filelist.length) > this.#max_files) {
            this.showError(`${this.#max_files} file uploads maximum.`);
            return false;
        }
        let total_size = 0;
        this.#droppedFiles.forEach((f) => {
            total_size += f.size;
        });

        const invalid_file_indexes = [];
        for (let i = 0; i < filelist.length; i++) {
            const file = filelist[i];

            if (this.#droppedFiles.length > 0) {
                // check for dupes by filename
                let dupe = false;
                this.#droppedFiles.forEach((f) => {
                    if (f.name === file.name) {
                        dupe = true;
                    }
                });
                if (dupe) {
                    invalid_file_indexes.push(i);
                    continue;
                }
            }

            // check for mime-type constraints
            if (this.#allowedMimeTypes.length > 0
                && this.#allowedMimeTypes.indexOf(file.type) === -1) {
                this.showError(`File: ${file.name} is not an allowed file type.`);
                invalid_file_indexes.push(i);
                continue;
            }

            if (this.#max_file_size > 0 && file.size > this.#max_file_size) {
                this.showError(`File: ${file.name} is greater than allowed file size of ${this.#bytesToMB(this.#max_file_size)} MB.`);
                invalid_file_indexes.push(i); // can't edit filelist so we just have to track which files to ignore later
            } else if (this.#batch_files === false && total_size + file.size > this.#max_upload_size) {
                this.showError(`File: ${file.name} causes files to go over maximum upload size of ${this.#bytesToMB(this.#max_upload_size)} MB at a time.`);
                invalid_file_indexes.push(i);
            } else {
                total_size += file.size;
            }
        }
        if (this.#batch_files === false && this.#max_upload_size > 0 && total_size > this.#max_upload_size) {
            this.showError(`Files total ${total_size} bytes which exceeds the maximum of ${this.#bytesToMB(this.#max_upload_size)} MB at a time.`);
            return false;
        }

        for (let x = 0; x < filelist.length; x++) {
            if (invalid_file_indexes.indexOf(x) !== -1) {
                continue;
            }
            this.#droppedFiles.push(filelist[x]);
        }
        if (this.#droppedFiles.length === 0) {
            return false;
        }
        return true;
    }

    /**
     * Create an array of batched files
     * @return {Array}
     */
    #batchFiles() {
        const b = [];
        const sizes = [];

        if (!this.#batch_files) {
            b.push(this.#droppedFiles);
            return b;
        }
        let batches = 1;
        b.push([]);
        sizes.push(0);
        this.#droppedFiles.forEach((file) => {
            let added = false;
            sizes.forEach((s, i) => {
                if (!added && (s + file.size) < this.#max_upload_size) {
                    sizes[i] = s + file.size;
                    b[i].push(file);
                    added = true;
                }
            });
            if (!added) {
                sizes.push(0);
                batches++;
                b.push([]);
                const i = batches - 1;
                sizes[i] = file.size;
                b[i].push(file);
            }
        });
        return b;
    }

    /**
     * When ajax request completes (success or error)
     * @param {Boolean} error was it an error
     */
    #completeRequest(error = false) {
        if ((this.#batch.current >= this.#batch.total) || error) {
            this.#form.removeAttribute('data-submitting');
            this.#stopProgress();
        } else {
            this.#batch.current++;
            this.#ajaxUpload();
        }
    }

    /**
     * Ajax upload event
     * For batches of files this will get called for each batch
     */
    #ajaxUpload() {
        // set current batch number (so processing scripts can deal appropriately)
        const curBatch = this.#form.querySelector('input[name="batch_current"]');
        if (curBatch) {
            curBatch.value = this.#batch.current;
        }

        const ajaxData = new FormData(this.#form);
        // Clear out empty form input... Safari hates the empty one.
        // Make sure delete is supported (it is in at least the latest Safari where we need it.)
        if (typeof ajaxData.delete === 'function' && this.#input) {
            ajaxData.delete(this.#input.getAttribute('name'));
        }
        // add the current batch of files to the formdata (if no batches this is just all the files)
        this.#batchedFiles[this.#batch.current - 1].forEach((file) => {
            if (!this.#input) {
                return;
            }
            ajaxData.append(this.#input.getAttribute('name'), file);
        });

        const xhr = new XMLHttpRequest();
        xhr.onload = () => {
            const status = xhr.status;
            let error = true;
            if (status >= 200 && status < 300) {
                try {
                    const data = JSON.parse(xhr.responseText);
                    if (this.#callback) {
                        this.#callback(data, this);
                    }
                    error = false;
                } catch {
                    // in case json is invalid
                    if (this.#error_callback) {
                        this.#error_callback(xhr, this);
                    }
                }
            } else if (this.#error_callback) {
                this.#error_callback(xhr, this);
            }
            this.#completeRequest(error);
        };
        xhr.onerror = () => {
            if (this.#error_callback) {
                this.#error_callback(xhr, this);
            }
            this.#completeRequest(true);
        };
        xhr.upload.onprogress = (ev) => {
            if (this.countFiles() > 0) {
                this.#updateProgress(ev.loaded, ev.total);
            }
        };
        xhr.open(
            this.#form.method,
            this.#form.action,
        );
        // only if we have files!
        if (this.countFiles() > 0) {
            this.#startProgress();
        }
        xhr.send(ajaxData);
    }

    /**
     * Drag drop handlers that repeat for many events
     * @param {Event} e various event types
     */
    #preventDragDropDefault(e) {
        e.preventDefault();
        e.stopPropagation();
        switch (e.type) {
            case 'dragover':
            case 'dragenter':
                this.#droparea.classList.add('is-dragover');
                break;
            case 'dragleave':
            case 'dragend':
            case 'drop':
                this.#droparea.classList.remove('is-dragover');
                break;
        }
    }

    /**
     * Drag/drop event handlers
     */
    #addDragDropEvents() {
        if (!this.#droparea) {
            return;
        }
        this.#droparea.classList.add('has-advanced-upload');

        // inject inputs for batch file data if appropriate
        if (this.#batch_files) {
            const input1 = document.createElement('INPUT');
            input1.setAttribute('type', 'hidden');
            input1.setAttribute('name', 'batch_current');
            input1.value = 0;
            const input2 = document.createElement('INPUT');
            input2.setAttribute('type', 'hidden');
            input2.setAttribute('name', 'batch_total');
            input2.value = 0;
            this.#form.prepend(input1, input2);
        }
        this.#droparea.addEventListener('drag', this.#preventDragDropDefault.bind(this));
        this.#droparea.addEventListener('dragstart', this.#preventDragDropDefault.bind(this));
        this.#droparea.addEventListener('dragend', this.#preventDragDropDefault.bind(this));
        this.#droparea.addEventListener('dragover', this.#preventDragDropDefault.bind(this));
        this.#droparea.addEventListener('dragenter', this.#preventDragDropDefault.bind(this));
        this.#droparea.addEventListener('dragleave', this.#preventDragDropDefault.bind(this));
        this.#droparea.addEventListener('drop', (e) => {
            this.#preventDragDropDefault(e);
            const adding = this.#addFiles(e.dataTransfer.files);
            this.#showFiles();
            if (adding === true && this.#autoupload) {
                const event = new SubmitEvent('submit', { cancelable: true });
                this.#form.dispatchEvent(event);
            }
        });

        this.#droparea.addEventListener('click', (e) => {
            // delegate for various functions
            const tags = ['A', 'I', 'SPAN', 'B', 'STRONG', 'BUTTON'];
            const tag = e.target.tagName;
            // links and possible children of links
            if (tags.indexOf(tag) !== -1) {
                const link = e.target.closest('a, button');
                if (link?.classList.contains('s-ui-filedrop-clear')) {
                    // clear files button
                    this.#clearFiles();
                    this.#removeError();
                }
            }
        });
    }

    /**
     * For when files are added using the normal input
     * @param {ChangeEvent} e File input change
     */
    #inputChange(e) {
        if (e.target.files) {
            const adding = this.#addFiles(e.target.files);
            this.#resetFileInput();
            if (adding === true && this.#autoupload) {
                const event = new SubmitEvent('submit', { cancelable: true });
                this.#form.dispatchEvent(event);
            }
            this.#input.classList.remove('has-focus');
            this.#showFiles();
        }
    }

    /**
     * Add events to the native file input
     */
    #addInputEvents() {
        if (!this.#input) {
            return;
        }
        this.#input.addEventListener('change', (e) => {
            this.#inputChange(e);
        });
        this.#input.addEventListener('focus', (e) => {
            e.target.classList.add('has-focus');
        });
        this.#input.addEventListener('blur', (e) => {
            e.target.classList.remove('has-focus');
        });
    }

    /**
     * Form submit
     * @param {SubmitEvent} e Form
     */
    #submitForm(e) {
        e.preventDefault();
        if (this.#form.dataset.submitting) {
            return;
        }

        this.#form.classList.remove('is-error');
        this.#removeError();

        if (this.#max_files > 0 && this.countFiles() > this.#max_files) {
            this.showError(`${this.#max_files} file uploads maximum.`);
            return;
        }

        if (this.#validate(this) === false) {
            this.#form.classList.add('is-error');
            return;
        }

        this.#form.dataset.submitting = 'true';

        // setup batch file
        this.#batch.current = 1;
        this.#batchedFiles = this.#batchFiles();
        this.#batch.total = this.#batchedFiles.length;

        const batchInput = this.#form.querySelector('input[name="batch_total"]');
        if (batchInput) {
            batchInput.value = this.#batch.total;
        }
        // this will iterate as necessary over the batches
        this.#ajaxUpload();
    }

    /**
     * Setup event handlers
     */
    init() {
        if (!this.#form) {
            return;
        }
        this.#form.addEventListener('submit', (e) => {
            this.#submitForm(e);
        });
        this.#addInputEvents();
        this.#addDragDropEvents();
        // Add a warning about max files size if the translation has been included
        if (this.#label && this.#translations.max_file_size) {
            const warning = document.createElement('p');
            warning.innerHTML = `${this.#translations.max_file_size}: ${this.#bytesToMB(this.#max_file_size)}MB`;
            this.#label.append(warning);
        }
    }
}
